import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api, { resolveAssetUrl } from "@/lib/api";
import useSEO from "@/hooks/useSEO";

const DEST_HERO_BG =
  "https://images.unsplash.com/photo-1517760444937-f6397edcbbcd?auto=format&fit=crop&w=2000&q=80";

const Destinations = () => {
  useSEO({
    title: "Destinations",
    description: "Explore handpicked tour packages — Ladakh, Kerala, Rajasthan, Goa, Bali, Dubai, Shirdi, Bhimashankar, Mahabaleshwar and more. Curated journeys by Shaurya Travels.",
    path: "/destinations",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/destinations")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="destinations-page">
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={DEST_HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 h-full flex flex-col justify-end pb-14 text-white">
          <p className="text-xs uppercase tracking-[0.4em] opacity-80 mb-4 fade-up">
            — Curated journeys
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.0] fade-up-2">
            Destinations
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg opacity-90 fade-up-3 font-light">
            A handpicked collection of journeys we craft — from quiet Himalayan
            retreats to coastal escapes and far-flung adventures abroad.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-20 pb-8">
        {loading ? (
          <div className="text-center py-24 text-sm uppercase tracking-[0.25em] text-[var(--st-ink-2)]">
            Loading destinations…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="destinations-empty"
            className="border border-dashed border-[var(--st-border)] py-24 text-center"
          >
            <p className="font-serif-display text-3xl mb-3">No destinations yet</p>
            <p className="text-sm text-[var(--st-ink-2)] mb-8">
              The owner is curating the next set of journeys.
            </p>
            <Link
              to="/admin/login"
              data-testid="destinations-empty-admin-link"
              className="inline-block px-6 py-3 text-xs uppercase tracking-[0.25em] btn-accent"
            >
              Sign in to add
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((d, idx) => (
              <DestinationCard key={d.id} d={d} idx={idx} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const DestinationCard = ({ d, idx }) => (
  <article
    data-testid={`destination-${d.id}`}
    className="bg-[var(--st-surface)] border border-[var(--st-border)] overflow-hidden group flex flex-col"
    style={{ animation: `fadeUp 0.7s ease-out ${idx * 0.08}s both` }}
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-[var(--st-bg)]">
      {d.image_url ? (
        <img
          src={resolveAssetUrl(d.image_url)}
          alt={d.name}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[var(--st-ink-2)] text-xs uppercase tracking-[0.25em]">
          {d.name}
        </div>
      )}
    </div>

    <div className="p-6 sm:p-7 flex-1 flex flex-col">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--st-accent)] mb-2">
        {d.region || "Journey"}
      </p>
      <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight mb-3">
        {d.name}
      </h3>
      <p className="text-sm text-[var(--st-ink-2)] leading-relaxed mb-5 flex-1">
        {d.short_description || d.description}
      </p>

      {d.tags?.length > 0 && (
        <div className="mb-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--st-ink-2)] mb-2">Highlights</p>
          <div className="flex flex-wrap gap-2">
            {d.tags.map((t) => (
              <span
                key={t}
                className="text-[11px] tracking-wider px-3 py-1 border border-[var(--st-border)] text-[var(--st-ink)]"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      <Link
        to={`/contact?destination=${encodeURIComponent(d.name)}`}
        data-testid={`destination-enquire-${d.id}`}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[var(--st-ink)] border-b border-[var(--st-ink)] pb-1 self-start hover:text-[var(--st-accent)] hover:border-[var(--st-accent)] transition-colors"
      >
        Book / Enquire <ArrowRight size={14} />
      </Link>
    </div>
  </article>
);

export default Destinations;
